import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLogin, useRegister } from '../../hooks/useAuth';
import { useConfig } from '../../hooks/useConfig';
import { useAuthModal } from '../../providers';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export function AuthModal() {
  const modal = useAuthModal();
  const { data: config } = useConfig();
  const login = useLogin();
  const register = useRegister();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showForgotNote, setShowForgotNote] = useState(false);
  const [googleError, setGoogleError] = useState(false);

  const isRegister = modal.mode === 'register';
  const mutation = isRegister ? register : login;

  // Бекенд редиректить сюди з ?authError=1 після невдалого /api/auth/google/callback
  // (протермінований state, відмова користувача, збій мережі) — показуємо модалку
  // з поясненням і одразу чистимо query-параметр, щоб він не лишався в URL.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once on mount only; modal.open isn't stable across renders and re-running on every modal state change would fight it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('authError')) return;
    setGoogleError(true);
    modal.open('login');
    params.delete('authError');
    const rest = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
  }, []);

  const reset = () => {
    setEmail('');
    setPassword('');
    setConfirm('');
    setShowForgotNote(false);
    setGoogleError(false);
    mutation.reset();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isRegister && password !== confirm) return;
    mutation.mutate(
      { email, password },
      {
        onSuccess: () => {
          modal.close();
          reset();
        },
      },
    );
  };

  return (
    <Dialog.Root
      open={modal.isOpen}
      onOpenChange={(open) => {
        if (!open) {
          modal.close();
          reset();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 animate-veil bg-black/60" />
        {/*
          Centering via flex on this wrapper, not `left-1/2 top-1/2 -translate-1/2` on
          Dialog.Content itself: `animate-fadeUp`'s keyframes set `transform: translateY(...)`
          literally, which replaces (not composes with) a static `-translate-x-1/2
          -translate-y-1/2` on the same element — the modal ends up with its top-left
          corner at the viewport center instead of actually centered.
        */}
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <Dialog.Content className="w-[min(400px,calc(100%-32px))] animate-fadeUp border border-line bg-surface-2 p-8">
            <Dialog.Title className="m-0 text-[22px] font-bold text-ink">
              {isRegister ? 'Створити акаунт' : 'Вхід'}
            </Dialog.Title>
            <p className="mono-label mt-1 mb-6">214° · ОСОБИСТИЙ КАБІНЕТ</p>

            {config?.googleAuthEnabled && (
              <>
                <a
                  href="/api/auth/google"
                  className="btn-press mb-4 flex w-full items-center justify-center gap-2 border-none bg-ink py-3 text-[14px] font-bold text-bg cursor-pointer"
                >
                  G Продовжити з Google
                </a>
                {googleError && (
                  <p className="mb-4 text-center text-[12px] text-warn">
                    Не вдалося увійти через Google. Спробуйте ще раз або скористайтесь email і
                    паролем.
                  </p>
                )}
                <p className="mono-label mb-4 text-center">АБО</p>
              </>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Input
                type="password"
                placeholder="Пароль"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              {isRegister && (
                <Input
                  type="password"
                  placeholder="Повторіть пароль"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              )}
              {isRegister && password !== confirm && confirm.length > 0 && (
                <p className="text-[13px] text-warn">Паролі не збігаються</p>
              )}
              {mutation.isError && (
                <p className="text-[13px] text-warn">{mutation.error.message}</p>
              )}
              <Button
                type="submit"
                variant="accent"
                size="lg"
                className="w-full"
                disabled={mutation.isPending}
              >
                {isRegister ? 'СТВОРИТИ АКАУНТ' : 'УВІЙТИ'}
              </Button>
              {isRegister && (
                <p className="text-center text-[12px] text-muted-2">
                  Створюючи акаунт, ви погоджуєтесь з{' '}
                  <Link
                    to="/privacy"
                    onClick={() => modal.close()}
                    className="text-muted underline"
                  >
                    Політикою конфіденційності
                  </Link>
                  .
                </p>
              )}
            </form>

            <button
              type="button"
              onClick={() => {
                modal.setMode(isRegister ? 'login' : 'register');
                reset();
              }}
              className="mt-4 block w-full border-none bg-transparent text-center text-[13px] text-accent cursor-pointer"
            >
              {isRegister ? 'Вже маю акаунт' : 'Створити акаунт'}
            </button>

            {!isRegister && (
              <button
                type="button"
                onClick={() => setShowForgotNote(true)}
                className="mt-2 block w-full border-none bg-transparent text-center text-[12px] text-muted-2 cursor-pointer"
              >
                Забули пароль?
              </button>
            )}
            {showForgotNote && (
              <p className="mt-2 text-center text-[12px] text-muted">
                Напишіть нам на пошту підтримки — відновимо доступ вручну.
              </p>
            )}
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
